import { MarkHeading } from '../../../components/MarkControls';
import * as layout from '../../../components/markLayout.module.css';
import {
    MarkChoiceLabel,
    MarkChoice,
    MarkInput,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import IrreversibleWarning from './IrreversibleWarning';
import VariantBuilder from './VariantBuilder';
import type { BlastDeliveryScope, BlastMessageClass, BlastVariantInput } from '../../../types';
type Step1Data = {
    name: string;
    messageClass: BlastMessageClass;
    deliveryScope: BlastDeliveryScope;
    variants: BlastVariantInput[];
};
type Props = {
    data: Step1Data;
    onChange: (data: Step1Data) => void;
    errors: string[];
};
const Step1Structure: React.FC<Props> = ({ data, onChange, errors }) => {
    const nameId = useId();
    return (
        <div className={layout.form}>
            <IrreversibleWarning />
            <div className={layout.field}>
                <MarkLabel htmlFor={nameId}>Name</MarkLabel>
                <MarkInput
                    id={nameId}
                    data-field="name"
                    type="text"
                    value={data.name}
                    onChange={(e) => onChange({ ...data, name: e.currentTarget.value })}
                    placeholder="e.g. Summer Campaign 2026"
                />
            </div>
            <fieldset aria-label="Message class" className={layout.fieldset}>
                <MarkHeading role="h6">Message class</MarkHeading>
                <div className={layout.choiceGroup}>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="messageClass"
                            value="MARKETING"
                            checked={data.messageClass === 'MARKETING'}
                            onChange={() => onChange({ ...data, messageClass: 'MARKETING' })}
                        />
                        Marketing
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="messageClass"
                            value="TRANSACTIONAL"
                            checked={data.messageClass === 'TRANSACTIONAL'}
                            onChange={() => onChange({ ...data, messageClass: 'TRANSACTIONAL' })}
                        />
                        Transactional
                    </MarkChoiceLabel>
                </div>
                {data.messageClass === 'TRANSACTIONAL' && (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-warning)',
                            marginTop: '0.4rem'
                        }}
                    >
                        The user who publishes or schedules will be auto-recorded as approver.
                        Bypasses unsubscribe suppression.
                    </P>
                )}
            </fieldset>
            <fieldset aria-label="Delivery scope" className={layout.fieldset}>
                <MarkHeading role="h6">Delivery scope</MarkHeading>
                <div className={layout.choiceGroup}>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="deliveryScope"
                            value="PER_EMAIL"
                            checked={data.deliveryScope === 'PER_EMAIL'}
                            onChange={() => onChange({ ...data, deliveryScope: 'PER_EMAIL' })}
                        />
                        Per email (one send per email address)
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="deliveryScope"
                            value="PER_ACCOUNT"
                            checked={data.deliveryScope === 'PER_ACCOUNT'}
                            onChange={() => onChange({ ...data, deliveryScope: 'PER_ACCOUNT' })}
                        />
                        Per account
                    </MarkChoiceLabel>
                </div>
            </fieldset>
            <VariantBuilder
                variants={data.variants}
                onChange={(variants) => onChange({ ...data, variants })}
            />
            {errors.length > 0 && (
                <div role="alert" className={layout.notice}>
                    {errors.map((err) => (
                        <P
                            key={err}
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {err}
                        </P>
                    ))}
                </div>
            )}
        </div>
    );
};
export type { Step1Data };
export default Step1Structure;
